/*
    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import ClockSync from 'clock/ClockSync.js';
import Midi from 'Midi';
import {MAP, DELETE, OFF} from 'constants/midi-map-modes.js';
import Resampler from 'track/Resampler.js';
import {MULTI_SYNTH} from 'constants/instrument-types.js';
import MetronomeTrack from 'track/MetronomeTrack.js';
import EnvelopesTrack from 'track/EnvelopesTrack.js';
import LFOsTrack from 'track/LFOsTrack.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import {PALETTE_EFFECT} from 'effects/live/constants.js';
import {sortTracksByDependencies} from 'effects/midi/scripting/dependencies.js';
import {PIANO_ROLL, WAVEFORM} from 'constants/arrangement-modes.js';
import {publishRoll, publishTrigger} from 'messaging/messages.js';
import {clearQueue} from 'messaging/queue.js';
import Scenes from 'scenes/Scenes.js';
import {FORWARD, REVERSE, FORWARD_REVERSE} from 'constants/playhead-directions.js';
import {getTriggerTimes} from 'keyboard/TriggerTimeHelper.js';
import DreamSampler from 'instruments/DreamSampler.js';
import ParameterContext from 'effects/ParameterContext.js';
import StepData from 'StepData';
import {PATTERN_SWITCH} from 'session/constants.js';
import {getStepFromPosition} from 'components/arrangement/getStepFromPosition.js';
import {triggerTracks} from './triggerTracks.js';
import Resolution from './Resolution.js';
import SequenceRollEvent from 'session/SequenceRollEvent.js';
import Rolling from 'keyboard/Rolling.js';
import Effects from 'Effects.js';
import axios from 'axios';
import Sound from 'components/Sound.js';
import Track from 'Track.js';
import SequencerEngine from 'SequencerEngine.js';
import Grouper from './Grouper.js';
import startaudiocontext from 'startaudiocontext';
import crop from 'Crop.js';
import AutomationScheduler from './AutomationScheduler.js';
import Pattern from 'Pattern.js';
import Sends from 'Sends.js';
import Sampler from 'Sampler.js';
import KeyboardHelper from 'keyboard/KeyboardHelper.js';
import {
    setActionMidiMapSelection,
    setTrackRolling,
    setResampleMaster,
    setTracksToResample,
    setIsRecording,
    setCurrentPattern,
    setCurrentStep,
    setCurrentAndGlobalSteps,
    setCurrentGlobalStep,
    setIsPlaying,
    setIsPlayingMix,
    setBPM,
    setPlayScheduled,
    trackRecording,
    trackPreparingToRecord,
    setArrangementLooping,
    setTrackToFreeze,
    setEffectParameterValues,
    setArrangementCursorStep
} from 'actions/index.js';
import {deleteSteps} from 'operations/actions.js';
import {recordStep} from 'operations/actions.js';
import {deleteStep} from 'operations/actions.js';
import {INSTRUMENT} from 'constants/track-types.js';
import LFOTrack from 'track/LFOTrack.js';
import TrackRollAction from 'midi/TrackRollAction.js';
import SequenceRollAction from 'midi/SequenceRollAction.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import {SLAVE} from 'constants/clock-types.js';
import {SEQUENCE_ROLL} from 'session/constants.js';
import {SEQUENCER_VIEW} from 'constants/view-modes.js';
import {ARRANGEMENT_VIEW} from 'constants/view-modes.js';
const defaultDuration = 1;
const SAMPLE_RATE = 44300.0;
const STABLE_LOOK_AHEAD = 80; // 80 ms

const TIME_16N = new Tone.Time('16n');
const TIME_32N = new Tone.Time('32n');

// A sub-class of SequencerEngine that sequences sounds via Tone.js
class PlayableSequencerEngine extends Grouper(AutomationScheduler(SequencerEngine)) {
    constructor(store, transport, aubio, stereoOnly, noMetronome, isOffline) {
        super(store, transport, aubio, stereoOnly, isOffline);

        // keeps track of the last time a step was made
        this.currentStepTime = null;
        this.previousStepTime = null;
        this.lastTrackStep = undefined;

        // tracksToRecord keeps track of which tracks we want to record
        // on the next "step 0" event
        this.tracksToRecord = {};

        // tracksRecording keeps track of which tracks are currently being recorded
        this.tracksRecording = {};

        // rolling manager helps deal with the "rolling" feature of the sqeuencer
        this.rollingManager = new Rolling();

        this.nextTimeScheduled = undefined;
        this.nextStepScheduled = undefined;
        this.startedTransportExplicitly = false;

        this.patternSwitchScheduled = null;

        // TODO: get rid of these variables since it's in redux and is
        // conflicting
        this.isRecording = false;
        this.playing = false;

        // when true we "beat repeat" the current portion of
        // the sequence
        this.sequenceRolling = false;

        this.resampler = undefined;

        this.automationStep = 0;

        this.bpmParameter= new AbstractAudioParamParameter(
            'BPM', this.transport.bpm,
            (o, x, automation) => this.onNewBpm(x, automation),
            {
                min: 1,
                max: 500,
                unit: '',
            });

        if (!window.explore) {
            this.bpmParameter.set(generateRandomBPM());
        } else {
            this.bpmParameter.set(Tone.Transport.bpm.value);
        }

        this.swingParameter= new AbstractParameter(
            'Swing', this,
            (o, x) => {
                this.onNewSwing(x);
            },
            {
                min: 50,
                max: 75,
                unit: '',
                defaultValue: 50
            });

        this.parameters = [];
        this.registerParameter(this.bpmParameter);
        this.registerParameter(this.swingParameter);

        this.latency = undefined;
        this.clockSync = new ClockSync(this);

        this.masterResampler = new Resampler(this, this.groups.channelMixer.stereoMerger, true);

        this.transpose = 0;
        this.autoResampleMode = false;

        this.metronome = !noMetronome && new MetronomeTrack(
            this,
            this.mixSession,
            1,
            () => []);

        this.deleting = false;
        this.scenes = new Scenes(this);
        this.parametersToUpdate = {};
    }

    registerParameter(parameter) {
        this.parameters.push(parameter);
        parameter.setContext(new ParameterContext().forSequencerEngine(this));
        let ret = parameter.setStore(this.store);
        this.dispatch(
            setEffectParameterValues(ret));
    }

    async startPlaySequence(time, syncing) {
        this.playSequence.cancel();
        this.playSequence.start(time);
        if (this.getSamplerPlaySequence) {
            this.getSamplerPlaySequence().start(time);
        }
    }

    get rolling() {
        return this.store.getState().sequencer.trackRolling;
    }

    schedulePlay() {
        if (!this.playing && this.clockSync.connected &&
           this.clockSync.clockType === SLAVE) {
            this.store.dispatch(setPlayScheduled(true));
        } else {
            this.play();
        }
    }

    // toggles playing of the sequence
    play(time, syncing=false) {
        if (this.keyboardHelper) {
            this.keyboardHelper.release = undefined;
        }
        this.playTimestamp = new Date().getTime();
        this.playing = !this.playing;
        this.latency = undefined;

        this.firstStep = true;
        this.trackStep = this.isLooping && this.state.mix.isPlayingMix ? this.loopStart : this.startStep;
        if (this.state.keyboard.cursor &&
            this.isShowingPianoRoll()) {
            this.trackStep = Track.baseResolution.transformStepAtResolution2(
                this.state.keyboard.cursor,
                this.displayResolution);
        }
        this.currentStep = 0;
        this.previousStep = 0;
        this.previousStepTime = undefined;
        this.currentStepTime = undefined;

        if (this.playing) {
            if (!syncing && !this.hasAtleastOneSequencerEnginePlaying()) {
                // this is the first sequence to be played (we
                // aren't syncing with another sequences bpm), so
                // we update the bpm to this sequences' own, saved bpm
                this.updateBpm(this.bpm);
            }

            this.startPlaySequence(time, syncing);

            if (syncing || !this.hasAtleastOneSequencerEnginePlaying()) {
                this.startedTransportExplicitly = true;
                this.transport.start('+0.00001');
                if (this.midi) {
                    this.midi.onPlay();
                }
            }

            this.restartAllMixes();
            clearQueue();

            // start any LFOs that need to be started (project-wide LFOs,
            // effect LFOs, and synth LFOs)
            for (let sub of this.subSequences) {
                sub.midiEffects.clear();
                // this.subSequences[i].lfosTrack.start();
                sub.tracks.forEach(track => track.restart());
                sub.tracks.forEach(track => track.start());
            }

            // update redux so the UI knows we are "playing"
            this.store.dispatch(setIsPlaying(this.getSequenceId(), true));
        } else {
            // otherwise we are stopping
            this.stop();
        }

        this.store.dispatch(setPlayScheduled(false));
    }

    restartAllMixes(isLooping=this.isLooping) {
        this.mixes.forEach(
            mix => mix.restart(isLooping));

        this.setSequenceRolling(false, undefined, true);
        for (let i in this.subSequences ){
            for (let j in this.subSequences[i].tracks) {
                let track = this.subSequences[i].tracks[j];
                track.patternRollStepNumber = undefined;
            }
        }
        this.schedulePatternBufferClear = false;
    }

    stop() {
        this.restartAllMixes();
        this.latency = undefined;
        this.playing = false;
        let trackStepReal = this.trackStepReal;
        this.trackStepReal = 0;

        this.playSequence.stop();
        this.startedTransportExplicitly = false;

        this.clearScheduledAutomations();

        if (!this.hasMultipleSequencerEnginesPlaying()) {
            // this is the last sequence to be stopped, so we stop the
            // entire transport
            this.transport.stop('+0.01');
            if (this.midi) {
                this.midi.onStop();
            }
        }
        
        // stop any LFOs needed
        for (let i in this.subSequences) {
            this.subSequences[i].lfosTrack.stop();
        }

        // stop any effects w lfo
        this.tracks.forEach(
            track => track.effects.filter(effect => effect.syncable())
                .forEach(effect => effect.stop()));

        // stop all midi effects (they might need to clear state)
        for (let sub of this.subSequences) {
            sub.midiEffects.stop();
            sub.tracks.forEach(
                t => {
                    // TODO: really need to move this to a track.stop() function
                    if (t.stepGroups.length > 0) {
                        // then we need to unset the transformation
                        t.pattern.transformStepBuckets = [];
                        t.pattern.isTransformed = false;
                        if (t.subSequence === this.currentSubSeuqence) {
                            t.updateSteps();
                        }
                    }

                    t.resetAnyPLockedParameters();
                    t.midiEffects.stop();
                    t.notesTriggered = {};

                    if (!t.isFrozen && t.instrument && t.instrument.releaseAll) {
                        t.instrument.releaseAll();
                    }

                    // for instrument racks
                    if (t.tracks) {
                        t.tracks.forEach(
                            ({track}) => track.midiEffects.stop());
                    }
                });
        }
        
        // update redux so UI knows we are no longer playing
        this.store.dispatch(setIsPlaying(this.getSequenceId(), false));
        if (this.state.sequencer.viewMode !== ARRANGEMENT_VIEW) {
            this.store.dispatch(setIsPlayingMix(false));
        }
        // this.store.dispatch(setCurrentGlobalStep(this.getSequenceId(), 0));

        if (this.clockSync) {
            this.clockSync.onStop();
        }

        for (let i in this.subSequences) {
            this.subSequences[i].tracks.forEach(
                t => {
                    t.chokes.chokingDuration = 0;
                    t.chokes.liveRelease();
                });
        }

        this.resetAutomationParameters();
        this.playSequence.cancel();
        this.loadNewSequence();
    }

    get startStep() {
        let viewMode = this.state.sequencer.viewMode;
        return this.state.arrangement.cursorStep  === undefined ||
            (viewMode === SEQUENCER_VIEW && !this.isLooping)  ?
            0 : 
            this.state.arrangement.cursorStep;
    }


    // called when user uses the sequencer clock to seek to
    // particular section of a track.
    seekToTrackStep(trackStep, switchPattern=true) {
        if (this.isLooping) {
            this.looping = false;
        }
        
        this.trackStep = trackStep;
        this.store.dispatch(setCurrentGlobalStep(this.getSequenceId(), trackStep));
        this.mixes.forEach(mix => mix.seekToTrackStep(trackStep));

        if (trackStep == 0 && switchPattern) {
            if (!this.isShowingPianoRoll()) {
                this.switchToPatternHelper(0);
            }
        }

        this.subSequences.forEach(
            sub => sub.tracks.forEach(t => t.scheduleTrackPatternChangeIfAny(this.trackStep, true)));
    }

    // creates a new blank sequence
    loadNewSequence() {
        if (this.playSequence) {
            this.playSequence.dispose();
        }
        this.playSequence = this.getPlaySequence();
    }

    async sleep(time) {
        return new Promise((resolve, reject) => {
            setTimeout(() => 
                       resolve(time), time);
        });
    }

    get isLooping() {
        return this.state.arrangement.looping;
    }

    set looping(x) {
        this.store.dispatch(setArrangementLooping(x));
    }

    get loopStart() {
        return this.state.arrangement.selection.start;
    }

    get loopEnd() {
        return this.state.arrangement.selection.end;
    }

    // schedules the events for the sequencer, where the
    // real meat and potatoes are...
    // returns a Tone.Sequence object that can be started/stopped etc
    getPlaySequence() {
        var steps = Array(16).fill(1).map((v, i) => { return i; });

        var playSequence = new Tone.Sequence((time, currentStep) => {
            time = this.handleLatency(time);
            let isFirstStep = this.firstStep;
            if (this.firstStep) {
                for (let i in this.subSequences) {
                    this.subSequences[i].lfosTrack.start(time);
                }

                this.firstStep = false;
            }

            // here is a hack, where if the lastTrackStep that has been scheduled
            // is the same as this track. In this case, we add 1. This can cause
            // problems. Seems to be working though!
            if (this.lastTrackStep === this.trackStep) {
                this.trackStep += 1;
            }
            
            this.nextTimeScheduled = time;

            // half of these aren't used, was trying to nail automation timing
            let automationStep =  this.automationStep;
            this.automationStep = this.getLoopedStep(this.trackStep);

            // first handle any events that should occur in a "mix session"
            // a mix session contains any pattern switches that might occur
            this.handleMixSessionStep(time);
            this.scenes.onNewStep(this.trackStep);

            // When playing several sequencer engines together, we 
            // want to sync the sequences so that they play in time with one another
            // this is used in the user page where they can play multiple sequences
            // and have them sync up.
            if (this.stepOneCallback !== undefined) {
                // HACK: why do we check different rawStep's depending on whether
                // we've startedTransposrtExplicitly? Needed for proper syncing tho.
                if (this.startedTransportExplicitly && currentStep == 15) {
                    // stepOneCallback basically tells the other sequencer
                    // engine to start
                    this.stepOneCallback(time);
                } else if (!this.startedTransportExplicitly && currentStep == 0) {
                    this.stepOneCallback(time);
                }
            }

            currentStep = this.trackStep; //  % this.length;

            if (this.scripting) {
                this.scripting.onStepReached(currentStep);
            }

            if (currentStep % this.length === 0) {
                this.scheduleAnyMasterResampling(time);
            }

            this.scheduleParametersToRecord(currentStep);

            if (isFirstStep) {
                this.scheduleParameterAutomations(currentStep, time, true);
            } else {
                if (this.isLooping && currentStep === this.loopStart) {
                    this.scheduleParameterAutomations(currentStep, time, true);
                } else {
                    this.scheduleParameterAutomations(currentStep, time, false);
                }
            }

            // schedule the triggers for all the tracks in the project
            for (let i in this.subSequences) {
                let subSequence = this.subSequences[i];
                if (this.state.mix.isPlayingMix) {
                    subSequence.updateVolumeFromAutomation(currentStep);
                }
                this.scheduleTracks([...subSequence.tracks], currentStep, time);
            }

            // metronome
            if (this.metronome) {
                this.scheduleTrack(this.metronome, currentStep, time)
                    .forEach(
                        trigger => 
                            this.metronome.trigger(trigger.stepData, trigger.time, currentStep));
            }
        

            // if the samples are "rolling" then we must schedule any tracks
            // that are being "played" right now by the user
            this.scheduleSamplesRolling(currentStep, time);
            // todo: have this be an input to the midi effects
            // chain then finally play it

            currentStep = currentStep % this.length;

            // now set a bunch of fields telling what step and time
            // we currently are at:
            // TODO: do we really need 10+ variables for all of this?
            this.nextStepScheduled = currentStep;
            let newTrackStep = this.getLoopedStep(this.trackStep + 1);
            let lastTrackStep = this.trackStep;
            this.lastTrackStep = this.trackStep;
            this.trackStep = newTrackStep;
            this.previousStepTime = this.latestStepTime;
            this.latestStepTime = time;
            let previousStepTime = new Tone.Time(this.latestStepTime);
            let previousStep = this.currentStep;
            this.currentStep = currentStep;
            this.previousStep = previousStep;

            this.clockSync.onTick(this.getMilliseconds(time), currentStep);

            if (this.midi) {
                this.midi.onTick(this.getMilliseconds(time), currentStep, this.resolution); 
            }

            this.dispatch(setEffectParameterValues(this.parametersToUpdate));
            this.parametersToUpdate = {};
            Tone.Draw.schedule(() =>  {
                if (this.playing) {
                    this.lastTrackStepReal = lastTrackStep;
                    this.previousStepReal = this.trackStepReal;
                    this.previousStepTimeReal = this.currentStepTime;
                    this.trackStepReal = lastTrackStep;
                    this.currentStepTime = time;
                    this.store.dispatch(setCurrentAndGlobalSteps(this.getSequenceId(), currentStep, this.trackStep));
                }
	    }, time);
        }, steps, '32n');
        playSequence.loop = true;
        return playSequence;
    }

    getLoopedStep(newTrackStep) {
        if (this.isLooping && this.state.mix.isPlayingMix) {
            let diff = newTrackStep - this.loopStart;
            let loopLength = this.loopEnd - this.loopStart;
            let modded = diff % loopLength;
            newTrackStep = this.loopStart + modded;
            return newTrackStep;
        }
        return newTrackStep;
    }

    handleLatency(time) {
        if (this.latency === undefined && !this.clockSync.initiator) {
            this.timeToCompare = this.timeToCompare - new Date().getTime();
            this.latency = this.timeToCompare - this.getMilliseconds(time);
            this.latency += TIME_32N.toMilliseconds();
            let latencyFromPlay = new Date().getTime() - this.playTimestamp;
            this.latency -= latencyFromPlay / 2;
        }
        if (this.latency) {
            time+=this.latency / 1000.0;
        }
        time += this.state.sequencer.latency / 1000.0;
        return time;
    }

    // handles all the necessary actions to be taken when
    // we are "playing" a mix. this includes automations,
    // pattern changes, erasing of events, etc.
    handleMixSessionStep(time) {
        // check if we are playing a full song's arrangement
        if (this.state.mix.isPlayingMix) {
            // check if we reached the loop start
            if (this.isLooping && this.trackStep === this.loopStart) {
                this.restartAllMixes(true);
            }

            // do we have any pattern switches?
            for (let i in this.subSequences) {
                let subSequence = this.subSequences[i];
                let patternSwitch = this.subSequences[i].mix.getValue(
                    PATTERN_SWITCH, this.automationStep);

                if (patternSwitch !== undefined &&
                    patternSwitch !== this.getCurrentPattern(subSequence)) {
                    this.switchToPattern(patternSwitch, true, subSequence, undefined, time);
                }
            }

            // check if we need to set the sequence rolling here, based on the mix
            let isSequenceRolling = this.isSequenceRollingAtStep(this.automationStep);
            if (isSequenceRolling !== undefined &&
                isSequenceRolling !== this.isSequenceRolling) {
                this.setSequenceRolling(isSequenceRolling, undefined, true);
            }
            
            try {
                // if we are playing a mix we should schedule any automation 
                // events that should occur on this step
                this.mixes.forEach(mix => mix.scheduleNext(this.trackStep, time));
            } catch (err) {
                console.log('caught err while scheduling automation = ');
                console.log(err);
            }
        } 

        // schedule all automations for patterns
    }

    scheduleParametersToRecord(currentStep) {
        if (!this.isRecording) {
            return;
        }

        let parameters = this.state.tracks.parametersToRecord;
        for (let i in parameters) {
            let parameter = parameters[i];
            if (parameters[i].context.track) {
                let track = parameter.context.track;
                let pattern = track.pattern;
                let patternStep = currentStep % track.length;
                let bucket = pattern.stepBuckets[patternStep];
                for (let j in bucket.steps) {
                    let step = bucket.steps[j];
                    if (!step.on) {
                        continue;
                    }
                    let stepParameters = step.parameters
                        .filter(param => param.name === parameter.name);
                    stepParameters.forEach(param => param.set(parameter.get()));
                }
                
            }
        }
    }

    // schedules triggers for all the tracks
    scheduleTracks(tracks, currentStep, time) {
        if (tracks === undefined) {
            return;
        }
        tracks = sortTracksByDependencies(tracks);
        let trackToTriggers = {};

        for (let track of tracks) {
            this.setGroupBits(track, currentStep);
        }

        for (var i=0; i < tracks.length; i++) {
            try {
                let track = tracks[i];
                let triggers = 
                    this.scheduleTrack(tracks[i], currentStep, time);
                trackToTriggers[track.trackNumber] = triggers;
                triggers.forEach(
                    x => publishTrigger(track, x));
            } catch (err) {
                console.log("caught error while scheduling track err = ");
                console.log(err);
            }
        }

        triggerTracks(this, tracks, trackToTriggers, time, currentStep);
        tracks.forEach(t => t.onStepEnded(time, currentStep));
    }
 
    // schedules all the triggers/events to occur at this step 
    // for a given track
    // todo: return triggers and process and then trigger
    scheduleTrack(track, currentStep, time) {
        if (track.disabled) {
            return [];
        }
        if (track.subSequence.isTrack) {
            track.scheduleTrackPatternChangeIfAny(currentStep);
        }

        let patternStep = track.getPatternStep(currentStep, true);
        let integerStep = Math.floor(patternStep);
        let timeOff = new Tone.Time('32n')*(patternStep-integerStep);

        let isTrackRecording = track.isEnabled;
        
        // schedule any pattern automations for this track
        try {
            track.pattern.mix.scheduleNext(integerStep, time, this);
        } catch (err) {
            console.log('caught an error while scheduling pattern events = ');
            console.log(err);
        }

        if (this.deleting) {
            track.deleteAtStep(integerStep);
        }
        
        if (integerStep == 0) {
            // when the patternStep is 0, we need to schedule
            // several things, because the pattern loop is "restarting"
            this.scheduleAnyFrozenTrackPlaying(track, time);
            this.scheduleAnyTrackRecording(track, time);
            this.scheduleAnyTrackResampling(track, time);
            this.scheduleAnyAudioTrackPlaying(track, time);
            this.scheduleAnyStepGroups(track, time);
        }

        // Depending on whether we are "sequence rolling" (i.e. beat repeating),
        // we need to schedule different track triggers
        if (!this.scheduleSequenceRollingIfNeeded(track, integerStep, time)) {
            // sequence is not rolling, so we schedule the usual track triggers
            if (!track.isMetronome && track.speedFactor > 1) {
                let triggers = [];
                for (let i=1; i <= track.speedFactor; i++ ){
                    triggers = [
                        ... triggers,
                        ... this.getTrackTriggers(
                            track, time + timeOff, patternStep, track.pattern, patternStep
                        )];
                    time += new Tone.Time('32n');
                    patternStep++;
                }
                return triggers;
            }
            let triggers = this.getTrackTriggers(track, time + timeOff, patternStep, track.pattern, patternStep);
            return triggers;
        } else {
            // otherwise we schedule track triggers using the steps from when
            // te "sequence rolling" was first triggered
            let resolution = track.getFactoredResolution();
            let steps = resolution.getNumberOfStepsAtBaseResolution();
            let stepNumber = track.patternRollStepNumber + patternStep % steps;
            stepNumber = stepNumber % track.pattern.stepBuckets.length;
            if (!resolution.isTriplet) {
                // then trigger as normal, only using this calculated stepNumber
                return this.getTrackTriggers(
                    track, time, stepNumber, track.pattern, patternStep);
            }

            // triplets are a little trickier
            let _stepNumber = Math.floor(stepNumber);
            let off = (stepNumber - _stepNumber)*Tone.Time('32n');
            if (track.isPlayheadReversed()) {
                off = -1 * off;
            }
            stepNumber = _stepNumber
            let triggerTimes = getTriggerTimes(stepNumber, resolution, true);
            let triggers = [];
            let i=0;
            for (let triggerTime of triggerTimes) {
                triggers = [
                    ... triggers,
                    ... this.getTrackTriggers(
                        track, time - off + triggerTime.time, stepNumber, track.pattern, patternStep, i === 0)];
                i++;
            } 
            if (triggers.length === 0) {
                return this.getTrackTriggers(
                    track, time - off, stepNumber, track.pattern, patternStep);
            }
           return triggers;
        }
    }

    // midi tracks and audio tracks allow recording of audio
    // from the "input" (i.e. a microphone, soundflower, interface)
    // this recording usually starts at step 0 of the track
    scheduleAnyTrackRecording(track, time) {
        let state = this.store.getState();

        // check if we should record any audio coming from midi tracks's
        // since it's step 0
        if (this.tracksToRecord[track.trackNumber]) {
            // we need to start recording this track
            track.startRecording(time);
            this.store.dispatch(trackRecording(track.trackNumber, true));
            this.tracksRecording[track.trackNumber] = true;
            delete this.tracksToRecord[track.trackNumber];
        } else if (this.tracksRecording[track.trackNumber]) {
            // we need to stop recording this track
            track.stopRecording(time);
            this.store.dispatch(trackRecording(track.trackNumber, false));
            delete this.tracksRecording[track.trackNumber];
        }
    }

    scheduleAnyMasterResampling(time) {
       let state = this.store.getState();
        // check if we're already resampling this track
        if (this.resampler !== undefined) {
            this.resampler.resamplingDurationElapsed++;

            // check if enough time has elapsed such that we should
            // stop resampling
            if (this.resampler.resamplingDurationElapsed ==
                state.tracks.resampleDuration) {
                // so we stop resampling
                this.resampler.stop(time);
                this.resampler = undefined;
            }
        }
        
        if (state.sequencer.resampleMaster) {
            // check if we should resample this track
            this.store.dispatch(setResampleMaster(false));
            this.resampler = this.masterResampler; 
            this.resampler.start(time);
        }
    }

    // schedules the starting or stopping of the resampling
    // of a track, if needed
    scheduleAnyTrackResampling(track, time)  {
       let state = this.store.getState();

        if (track.subSequence !== this.currentSubSequence) {
            return;
        }

        // check if we're already resampling this track
        if (track.resampler !== undefined) {
            track.resampler.resamplingDurationElapsed++;

            // check if enough time has elapsed such that we should
            // stop resampling
            if (track.resampler.resamplingDurationElapsed ==
                state.tracks.resampleDuration) {
                // so we stop resampling
                track.resampler.stop(time);
                track.resampler = undefined;
            }
        }
        
        // check if we should resample this track
        let tracksToResample = state.tracks.tracksToResample;
        let indexOfTrackToResample = tracksToResample.indexOf(track.trackNumber);
        if (indexOfTrackToResample > -1) {
            tracksToResample.splice(indexOfTrackToResample, 1);
            this.store.dispatch(setTracksToResample(tracksToResample));
            track.resampler = track.masterResampler;
            track.resampler.start(time);
        }

        if (state.tracks.trackToFreeze &&
            state.tracks.trackToFreeze.trackNumber === track.trackNumber &&
            state.tracks.trackToFreeze.subSequenceId === track.subSequence.id) {
            track.resampler = track.masterResampler;
            track.resampler.start(time, track, true);
            this.store.dispatch(setTrackToFreeze(undefined));
        }
        
    }

    scheduleAnyFrozenTrackPlaying(track, time) {
    }

    // Midi tracks can contain recorded audio, and when the step is 0
    // we need to play any recordings that need to be played
    scheduleAnyAudioTrackPlaying(track, time) {
        if (track.getTrackType() === 'MIDI' &&
            Object.keys(track.samplers).length > 0) {
            let stepData = new StepData();
            stepData.loadDefaults();
            stepData.attack = 80;
            stepData.release = TIME_16N.toMilliseconds() / 4;
            track.triggerSamples(stepData, time);
        }
    }

    scheduleSequenceRollingIfNeeded(track, stepNumber, time) {
        let state = this.store.getState();
        if (this.schedulePatternBufferClear) {
            track.patternRollStepNumber = undefined;
            this.schedulePatternBufferClear = false;
            return false;
        }

        if (!this.isSequenceRolling || !track.rolling) {
            // not needed since we're not "sequence rolling"
            return false;
        }

        track.updatePatternRollStepNumber(this.rollingResolution);

        return true;
    }

    // schedules any samples, midi, audio to be triggered at this time,
    // for this track. If you're looking for where the sequencerEngine
    // actually triggers samples, this is it.
    getTrackTriggers(track, time, sampleStep, pattern, actualStep, first=true) {
        let isReversed = track.isPlayheadReversed(actualStep);
        sampleStep = this.getStepNumber(sampleStep, actualStep);
        var bucket = pattern.getBucket(sampleStep);
        if (first) {
            track.onNewStep(sampleStep, time);
        }

        let steps = bucket ? bucket.steps : [];
        let toDelete = [];

        if (track.isFrozen) {
            if (sampleStep === 0) {
                track.triggerFrozenSample(time);
            }
        } else {
            // go through all steps inside this bucket (may contain unquantized
            // events, or more granular events...)

            for (var timeInBucket in steps) {
               // get the step we need to schedule
                let stepData = steps[timeInBucket]; 
                if (isReversed) {
                    timeInBucket = -1 * timeInBucket;
                }

                // calculate the time to play this step, based on the
                // bucket's time, and the current time.
                // Note: a bucket's key is a ratio of a 1/32th step. For example,
                // a key of 0.5 means we play this step 1/64th from now.
                let stepTime = new Tone.Time(time) + (TIME_32N * timeInBucket);
                
                // there may be several steps inside this step, i.e. a polyphonic
                // chord of individual StepData objects, to each be played
                let polyphony = stepData.getPolyphony();

                // order the triggers so slide come last
                polyphony = [... polyphony.filter(x => !x.slide), ... polyphony.filter(x => x.slide)];

                for (let i in polyphony) { 
                    let polyphonicStep = polyphony[i];
                    if (track.deleting) {
                        toDelete.push(polyphonicStep);
                        continue;
                    }

                    if (this.isStepMasked(polyphonicStep)) {
                        continue;
                    }

                    // send it through the midi effects 
                    let {time, step} = this.transformStep(polyphonicStep, stepTime, actualStep);
                    if (!step.slide) {
                        track.handlePLockedAutomations(step, time);
                    }
                    track.midiEffects.input(
                        step, time, actualStep);
                }
            }
        }

        if (track.deleting) {
            if (toDelete.length > 0) {
                this.operationManager.dispatch(deleteSteps(
                    track, toDelete));
            }
            return [];
        }

        // finally trigger the events that the midi effects chain
        // outputed
        // todo: figure out what to do about lost notes
        let _triggers = track.midiEffects.output(time, sampleStep, actualStep);

        // make sure that they contain a "track" associated with it
        let triggers= _triggers
            .filter(x => x.stepData && x.stepData.track);
        if (!this.shouldPlayStep(actualStep)) {
            return [];
        }

        // find the palette for the trigger
        // TODO: remove this entire concept since p-locks are better
        if (triggers.length > 0 && triggers[0].stepData.trackData) {
            let palette = triggers[0].stepData.palette;
            if (palette !== undefined) {
                palette = Math.floor(palette);
                let effect = track.liveEffects.effects.find(x => x.effect === PALETTE_EFFECT);
                if (effect) {
                    palette = palette % effect.palettes.length;
                }
            }
            if (this.state.effects.sequencedPalettes[track.id] !== palette &&
               this.state.effects.selectedPalettes[track.id] === undefined) {
                // find live effect to do this at
                let effect = track.liveEffects.effects.find(x => x.effect === PALETTE_EFFECT);
                if (effect) {
                    effect.switchToPalette(palette, undefined, time);
                }
            }
        }
        return triggers;
    }

    shouldPlayStep(stepNumber) {
        stepNumber = stepNumber % 32;
        stepNumber = Math.floor(stepNumber / 2);
        return this.state.steps.masterStepToggles[stepNumber];
    }

    scheduleTrackRolling(track, currentStep, time) {
        if (!track.subSequence.isTrack) {
            currentStep = track.getPatternStep(currentStep);
        }
        let millisecondsUntilStep = this.getMilliseconds(time);
        
        // we set this to timeout of millisecondsUntilStep - 10 ms so that
        // the user can cancel a roll up to 10 ms before the next step before
        // that step includes it
        let resolution = track.getFactoredResolution();

        setTimeout(() => {
            let triggers = this.rollingManager.getTriggers(currentStep, resolution, track);
            
            if (triggers.filter(x => x.trackSlice.trackNumber === track.trackNumber).length > 0) {
                track.onNewStep();
            } else {
                return;
            }
            triggers.forEach(trigger => {
                track = this.tracks[trigger.trackSlice.trackNumber];
                if (track === undefined) {
                    return;
                }
                let stepData = new StepData();
                stepData.slice = trigger.trackSlice.sliceNumber;
                stepData.velocity = track.trackData.velocity;
                stepData.transpose = trigger.trackSlice.transpose === undefined ?
                    track.trackData.transpose : trigger.trackSlice.transpose + track.trackData.transpose;
                stepData = track.getStepDataForStep(stepData);
                let newTime = new Tone.Time(time) + trigger.time;
                stepData.track = track;
                track.midiEffects.input(
                    stepData, newTime, currentStep);
                if (this.isRecording || (track.subSequence.isTrack && this.isRecordingMix)) {
                   track.recordHit(
                        trigger.step, resolution, stepData.slice,
                       null, stepData.transpose, stepData.velocity, this.keyboardHelper.release);
                }
            });
            triggers = track.midiEffects.output(time, currentStep);
            let tracks = [track];
            let tracksToTrigger = {};
            tracksToTrigger[track.trackNumber] = triggers;
            triggerTracks(this, tracks, tracksToTrigger, time, currentStep);
            track.onStepEnded(time, currentStep);
        }, millisecondsUntilStep - 20);
    }

    // schedules any sample triggers currently being triggered by 
    // the user with "roll" enabled.
    scheduleSamplesRolling(currentStep, time) {
        this.tracks
            .filter(track => track.rolling)
            .forEach(
                track =>
                    this.scheduleTrackRolling(track, currentStep, time));
    }

    missedNextScheduledStep() {
        return Tone.Master.context.currentTime <= this.nextTimeScheduled;
    }

    // schedules a sampler to trigger on the very next step this 
    // is used in the "rolling" mode, when the user hits a trigger and we 
    // want it to start triggering on the very next step.
    scheduleNext(trackNumber, sliceNumber, transpose) {
        let track = this.tracks[trackNumber]
        if (track.getTrackType() === INSTRUMENT) {
            return;
        }

        // we use the variable nextStepScheduled, which keeps track of the time
        // of the next step that has been already scheduled to be triggered
        let triggerTimes = this.rollingManager.getTriggerTimes(
            this.nextStepScheduled, this.resolution, track);

        let resolution = track.getFactoredResolution();
        // go through the trigger times for this roll event
        triggerTimes.forEach(obj => {
            let stepData = new StepData();
            stepData.slice = sliceNumber;
            stepData.transpose = transpose === undefined ?
                track.trackData.transpose :
                transpose + track.trackData.transpose;
            stepData = track.getStepDataForStep(stepData);

            track.trigger(stepData, this.nextTimeScheduled + obj.time);

            if (this.isRecording) {
                track.recordHit(
                    obj.step,
                    resolution,
                    sliceNumber,
                    stepData.duration,
                    stepData.transpose);
                track.updateSteps();
            }
        });
    }

    getCurrentStepTime() {
        let timeToStep = this.getMilliseconds(this.latestStepTime);
        let previousStepTime = 
            new Tone.Time(Tone.Master.context.currentTime).toMilliseconds() - 
            new Tone.Time(this.previousStepTime).toMilliseconds(); 

        if (previousStepTime < 0 || (timeToStep > 0 && timeToStep > previousStepTime)) {
            return this.previousStepTime;
        }
        return this.latestStepTime;
    }

    // gets the current step at the given resolution
    getCurrentStep(resolution, track) {
        // latest vs previous step time
        let timeToStep = 
            Math.abs(new Tone.Time(Tone.Master.context.currentTime) - 
                     new Tone.Time(this.latestAbsoluteTime)); 
        let previousStepTime = 
            Math.abs(new Tone.Time(Tone.Master.context.currentTime) - 
                     new Tone.Time(this.previousAbsoluteTime)); 

        let length = undefined;
        if (track) {
            if (track.subSequence.isTrack) {
                length = 1000000;
            } else {
                length = track.pattern.length();
            }
        } else {
            length = this.length;
        }
        let trackStep = this.lastTrackStep; 
        let currentStep = trackStep % length;
        if ((timeToStep > 0 && timeToStep > previousStepTime)) {
            // currentStep = this.previousStep;
        }
        resolution = resolution || Track.baseResolution;
        var current = Math.floor(
            currentStep /
                (Track.baseResolution.numSteps() / resolution.numSteps()));
        return current;
    }

    // returns the milliseconds until a given time, using
    // the audio context's current time
    getMilliseconds(time) {
        return new Tone.Time(time).toMilliseconds() -
            new Tone.Time(Tone.Master.context.currentTime).toMilliseconds();;
    }

    // tells us that we want to record a given track
    // at the next step 0.
    recordTrack(trackNumber) {
        this.tracksToRecord[trackNumber] = true;
    }

    // returns whether we are currently recording a given track
    isTrackRecording(trackNumber) {
        return this.tracksRecording[trackNumber];
    }

    doubleTrackLength(trackNumber) {
        this.tracks[trackNumber].doubleTrackLength();
        this.transport.loopStart = '0:0:0';
        this.transport.loopEnd = this.length / 32 + ":0:0"; 
    }

    halfTrackLength(trackNumber) {
        this.tracks[trackNumber].halfTrackLength();

        this.transport.loopStart = '0:0:0';
        this.transport.loopEnd = this.length / 32 + ":0:0"; 
    }

    set trackRolling(rolling) {
        if (this.state.midi.midiMapMode === MAP) {
            this.store.dispatch(setActionMidiMapSelection(
                new TrackRollAction(this)));
        }

        this.store.dispatch(setTrackRolling(rolling));
        this.rollingManager.clear();
    }

    setSequenceRolling(sequenceRolling, resolution = this.rollingResolution, isAutomation=false) {
        publishRoll(sequenceRolling, resolution.resolution, resolution.isTriplet);
        if (this.state.midi.midiMapMode === MAP) {
            this.store.dispatch(setActionMidiMapSelection(
                new SequenceRollAction(this)));
        }

        if (!sequenceRolling) {
            this.schedulePatternBufferClear = true;
        } else if (sequenceRolling != this.isSequenceRolling) {
            let numSteps = resolution.getNumberOfStepsAtBaseResolution();
            let stepNumber = this.automationStep;
            stepNumber -= stepNumber % numSteps;
            for (let i in this.subSequences) {
                this.subSequences[i].tracks.forEach(track => track.initPatternBuffer(stepNumber));
            }
            if (isAutomation) {
                this.trackRolling = true;
            }
        }
        this.isSequenceRolling = sequenceRolling;

        if (isAutomation) {
            return;
        }

        if (this.store.getState().mix.isRecordingMix) {
            let resolution = this.resolution;
            this.mix.addEvent(new SequenceRollEvent(sequenceRolling, resolution));
        } else if (this.store.getState().mix.isPlayingMix) {
            this.mix.holdEvent(new SequenceRollEvent(sequenceRolling, this.resolution));
        }
    }

    get isRecordingMix() {
        return this.store.getState().mix.isRecordingMix 
    }

    isSequenceRollingAtStep(step) {
        if (this.store.getState().mix.isPlayingMix) {
            let sequenceRolling = this.mix.getValue(
                SEQUENCE_ROLL, step);
            let previousValue = this.mix.getValue(
                SEQUENCE_ROLL, step - 1);
            /*
             * Note: this made recording mixes with rolling impossible 
            if (previousValue && !sequenceRolling) {
                this.trackRolling = false;
            }
            */
            return sequenceRolling;
        }

        return this.isSequenceRolling;
    }

    // sequenceRolling is essentially "beat repeat" in other
    // sequencers/drum machines. it "rolls" the sequence at
    // whatever step it was when sequenceRolling was triggered`
    set sequenceRolling(sequenceRolling) {
        this.setSequenceRolling(sequenceRolling);
    }

    get lfos() {
        return this.lfosTrack.lfos;
    }

    get envelopesTrack() {
        return this.currentSubSequence.envelopesTrack;
    }

    get lfosTrack() {
        return this.currentSubSequence.lfosTrack;
    }

    get envelopes() {
        return this.envelopesTrack.envelopes.map(
            envTrack =>
                envTrack.envelope);
    }

    async onNewBpm(bpm, isAutomation=false) {
        if (bpm !== undefined && bpm < 500 && bpm >= 1) {
            if (!isAutomation) {
                this.transport.bpm.value = bpm;
            }
            this.store.dispatch(setBPM(bpm));
            for (let i in this.subSequences) {
                this.subSequences[i].lfosTrack.onBPMUpdated(); 
            }
            this.tracks.forEach(track => track.onBPMUpdated());
            if (this.clockSync) {
                this.clockSync.onBPMChanged(bpm);
            }
       }
    }

    get bpm() {
        return this.bpmParameter.get();
    }

    set bpm(b) {
        this.bpmParameter.set(b, false);
    }

    get swing() {
        return this.swingParameter.get();
    }

    set swing(s) {
        this.swingParameter.set(s);
    }

    async updateBpm(bpm, syncing=false) {
        this.bpm = bpm;
        if (!syncing) {
            for (let i in this.subSequences) {
                this.subSequences[i].lfosTrack.onBPMUpdated(); 
            }
            this.tracks.forEach(track => track.onBPMUpdated());
        }
    }

    async onNewSwing(swingPercentage) {
        var swing = (swingPercentage - 50.0) / 50.0;
        this.transport.swing = swing;
    }

    updateSwing(swingPercentage) {
        this.swing = swingPercentage;
    }

    recording(recording) {
        // tell midi steps it is recording
        this.store.dispatch(setIsRecording(recording));

        this.isRecording = recording;

        this.tracks
            .filter(
                track => track.getTrackType() === 'MIDI')
            .map(
                track => track.recording(recording));
    }

    connectToKeyboardEngine(keyboardEngine) {
        this.keyboardEngine = keyboardEngine;
        this.keyboardHelper = new KeyboardHelper(this, keyboardEngine);
        this.midi = new Midi(keyboardEngine, this.keyboardHelper, (bpm) => this.updateBpm(bpm));
        this.midi.setStartCallback(() => this.play());
        this.midi.setStopCallback(() => this.stop());
    }

    // returns whether there are multiple sequencer engines
    // playing. this happens when we are in the "user" view
    // playing mulitple songs synced together
    hasMultipleSequencerEnginesPlaying() {
        let isPlaying = this.store.getState().sequencer.isPlaying;
        let count = Object.keys(isPlaying).filter(
            id => isPlaying[id]).length;
        return count > 1;
    }

    hasAtleastOneSequencerEnginePlaying() {
        let isPlaying = this.store.getState().sequencer.isPlaying;
        let count = Object.keys(isPlaying).filter(
            id => isPlaying[id]).length;
        return count > 0;
    }

    // getter for redux state
    get state() {
        return this.store.getState();
    }

    getStepNumber(patternStep, actualStep) {
        // how can this be scriptable?
        if (this.stepNumberScript) {
            return this.stepNumberScript(patternStep);
        }
        return patternStep;
    }

    get isLoading() {
        let loadingSequence = this.state.sequencer.loadingSequence;

        return Object.keys(loadingSequence).some(
            (s) => loadingSequence[s] === true);
    }

    sustain(value) {
        // get enabled tracks
        let toggledTracks = this.tracks.filter(
            track =>
                track.isEnabled);

        for (let track of toggledTracks) {
            let ampEnvelope = undefined;
            if (track.getTrackType() === INSTRUMENT) {
                let instrument = track.instrument;
                if (instrument.getInstrumentType() === MULTI_SYNTH) {
                    ampEnvelope = instrument.ampEnvelope;
                } else {
                    ampEnvelope = instrument;
                }
            } else {
                continue;
            }
            let kbd = this.keyboardHelper;
            let release = 0;
            if (value === 127) {
                // find any step that is being recorded and set the release parameter there
                ampEnvelope.releaseParameter.set(2500, true);
                release = 2500;
                
            } else {
                ampEnvelope.releaseParameter.set(100, true);
                release = 100;
            }

            kbd.release = release;

            // for the triggers that are already under way
            for (let t in kbd.recordedTriggers) {
                let trg = kbd.recordedTriggers[t];
                if (trg.recordedTriggers) {
                    trg.recordedTriggers.forEach(
                        x => x.release = release);
                }
            }
        }
    }

    isShowingPianoRoll() {
        return this.state.sequencer.showPianoRoll && this.state.arrangement.bottomMode === PIANO_ROLL;
    }
}

const generateRandomBPM = () => {
    return 60 + Math.floor(Math.random()*120);
}
 

export default PlayableSequencerEngine;
